import React from 'react';
import useAudioRecorder from '../../../../Hooks/audio/useAudioRecorder';
import useAudioStream from '../../../../Hooks/audio/useAudioStream';
import { useOrganisationData } from '../../../../Hooks/useOrganisation';
import { AudioConfig } from '../../../AudioConfigContext/Hooks/useAudioConfig';

function useRestart(condition: boolean): [boolean, () => void] {
  const [enabled, setEnabled] = React.useState(condition);
  React.useEffect(() => {
    setEnabled(condition);
  }, [condition]);

  const enabledRef = React.useRef(enabled);
  React.useEffect(() => {
    if (condition && !enabled && enabledRef.current) {
      setTimeout(() => {
        setEnabled(true);
      }, 100);
    }
    enabledRef.current = enabled;
  }, [enabled, condition]);
  return [enabled, () => { setEnabled(false); }];
}

export default function useAudioCapture(inProgress: boolean, audioConfig: AudioConfig): [Uint8Array | null, () => void] {
  const [lastAudioBlob, setLastAudioBlob] = React.useState<Uint8Array | null>(null);
  const { enableCoreAsrApi: settingsEnabled } = useOrganisationData();

  const [enabled, restart] = useRestart(inProgress && settingsEnabled);
  const enabledRef = React.useRef(enabled);

  React.useEffect(() => { enabledRef.current = enabled; }, [enabled]);

  function callback(data: Uint8Array) {
    if (enabledRef.current) {
      setLastAudioBlob(data);
    } else {
      setLastAudioBlob(null);
    }
  }

  const stream = useAudioStream(!!enabled, audioConfig);
  useAudioRecorder(stream, (data) => { callback(new Uint8Array(data)); });

  React.useEffect(() => {
    if (!enabled) {
      setLastAudioBlob(null);
    }
  }, [enabled]);

  return [lastAudioBlob, restart];
}
